'use client';

import { useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import styled from '@emotion/styled';
import Image from 'next/image';
import { color, borderRadius, spacing, fontSize } from '@/theme/tokens';
import { breakpoints } from '@/theme/tokens';
import { useTranslations } from 'next-intl';
import dayjs from 'dayjs';
import { MDXProvider } from '@mdx-js/react';
import { newsArticles } from '@/content/company/news/en';
import { getMDXComponents } from '@/components/common/MDXComponents';

const Wrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: ${spacing[16]};
`;

const NewsItem = styled(Box)`
    background-color: ${color.white[0]};
    border-radius: ${borderRadius[8]};
    padding: ${spacing[16]};
    display: flex;
    gap: ${spacing[32]};
    align-items: center;

    @media (min-width: ${breakpoints.sm}) {
        padding: ${spacing[32]};
    }

    @media (max-width: ${breakpoints.md}) {
        flex-direction: column;
        gap: ${spacing[16]};
        align-items: flex-start;
    }
`;

const NewsContent = styled(Box)`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: ${spacing[8]};
`;

const NewsText = styled(Box)`
    flex: 1;
`;

const NewsImage = styled(Box)`
    position: relative;
    width: 400px;
    max-width: 400px;
    aspect-ratio: 400/267;
    overflow: hidden;
    background-color: ${color.gray[10]};

    @media (max-width: ${breakpoints.md}) {
        width: 100%;
        max-width: none;
        order: -1;
        margin-bottom: ${spacing[8]};
    }
`;

const LoadMoreButton = styled(Button)`
    background-color: ${color.brand[50]};
    color: ${color.white[0]};
    border-radius: ${borderRadius[8]};
    padding: ${spacing[12]} ${spacing[20]};
    font-size: ${fontSize[15]};
    height: 46px;
    font-weight: 500;
    text-transform: none;
    align-self: center;

    &:hover {
        background-color: ${color.brand[60]};
    }

    &:disabled {
        background-color: ${color.gray[40]};
        color: ${color.gray[60]};
    }
`;

const ITEMS_PER_PAGE = 3;

const formatDate = (dateString: string) => {
    const date = dayjs(dateString);
    return date.format('MMMM D, YYYY');
};

export default function CompanyNews() {
    const t = useTranslations('CompanyPage');
    const [displayedItems, setDisplayedItems] = useState(ITEMS_PER_PAGE);
    const [loading, setLoading] = useState(false);

    const handleLoadMore = () => {
        setLoading(true);
        setTimeout(() => {
            setDisplayedItems(prev => Math.min(prev + ITEMS_PER_PAGE, newsArticles.length));
            setLoading(false);
        }, 500);
    };

    const hasMoreItems = displayedItems < newsArticles.length;

    return (
        <Wrapper>
            {newsArticles.length === 0 ? (
                <Typography variant="paragraph4regular" color={color.gray[60]}>
                    {t('noNews')}
                </Typography>
            ) : (
                <>
                    {newsArticles.slice(0, displayedItems).map((NewsComponent, index) => {
                        // Only first 3 images get priority (initially visible)
                        const shouldPrioritize = index < ITEMS_PER_PAGE;

                        return (
                            // Static, non-reordered list built from derived data — index keys are stable here.
                            // react-doctor-disable-next-line no-array-index-as-key
                            <NewsItem key={index}>
                                <NewsContent>
                                    <Typography variant="label5regular">
                                        {formatDate(NewsComponent.frontmatter.date)}
                                    </Typography>

                                    <Typography component="h2" variant="heading2bold">
                                        {NewsComponent.frontmatter.title}
                                    </Typography>

                                    <NewsText>
                                        <MDXProvider
                                            components={getMDXComponents({
                                                customLink: false,
                                                paragraphMargin: {
                                                    base: spacing[8],
                                                },
                                                listMargin: {
                                                    base: spacing[16],
                                                    sm: spacing[20],
                                                    md: spacing[24],
                                                },
                                            })}
                                        >
                                            <NewsComponent.default />
                                        </MDXProvider>
                                    </NewsText>
                                </NewsContent>

                                {NewsComponent.frontmatter.image && (
                                    <NewsImage>
                                        <Image
                                            src={NewsComponent.frontmatter.image}
                                            alt={NewsComponent.frontmatter.title}
                                            fill
                                            priority={shouldPrioritize}
                                            sizes="(max-width: 1199px) 100vw, 400px"
                                            style={{
                                                objectFit: 'cover',
                                                objectPosition: '50% 50%',
                                            }}
                                        />
                                    </NewsImage>
                                )}
                            </NewsItem>
                        );
                    })}

                    {hasMoreItems && (
                        <LoadMoreButton onClick={handleLoadMore} disabled={loading}>
                            {loading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                t('moreNews')
                            )}
                        </LoadMoreButton>
                    )}
                </>
            )}
        </Wrapper>
    );
}
